import React = require('react')
import { nl } from 'date-fns/locale'
import { Set } from 'immutable'
import DatePicker, { registerLocale } from "react-datepicker"
import { GroupedOverviewFilterOptions } from '../../../../backendApi/generated'
import { features } from '../../../../features'
import { isSmallViewport } from '../../../../utils/browser'
import { moment } from '../../../../utils/moment'
import { slugify } from '../../../../utils/string'
import { debouncedPush, nextMonth, nextWeek, pushEventAction, tomorrow } from '../../EventUtils'
import { EventsOverviewClassNames, FilterSettings, FilterUpdaters } from '../EventOverviewState'

registerLocale('nl', nl)

export const EventOverviewFilter = (props: {
  filterOptions: GroupedOverviewFilterOptions
  filterSettings: FilterSettings
  filterUpdaters: FilterUpdaters
  filterExpanded: boolean
  eventsOverviewClassNames: EventsOverviewClassNames
}) => {
  const {
    filterOptions,
    filterSettings: { query, regions, locations, tags, from },
    filterUpdaters: { updateQuery, updateRegions, updateLocations, updateTags, updateFrom, resetFilter, toggleFilterVisibility },
    filterExpanded,
    eventsOverviewClassNames,
  } = props

  const config = features().events
  if (config == 'off') return <></>
  const { locationFilter } = config.overviewPage

  const push = pushEventAction()

  const smallViewport = isSmallViewport()

  const activeTags = Set<string>(tags)
  const activeRegions = Set<string>(regions)
  const activeLocations = Set<string>(locations)

  const filterToggleText = filterExpanded ? 'Sluit filter' : 'Open filter'
  const now = new Date()

  return (
    <>
      {smallViewport && (
        <button
          className="a-filter__button button black"
          aria-controls="filter-content"
          aria-expanded={filterExpanded}
          onClick={() => {
            push(filterToggleText)
            toggleFilterVisibility()
          }}
        >
          {filterToggleText}
        </button>
      )}

      <div
        id="filter-content"
        className="activities__filter a-filter"
        aria-hidden={smallViewport ? !filterExpanded : null}
      >
        <div className="a-filter__header">
          <h3 className="a-filter__title title--ms">Filter op</h3>
          <button className="a-filter__reset" onClick={() => {
            push('Wis filter')
            resetFilter()
          }}>
            Wis alle filters
          </button>
        </div>

        <div className="a-filter__group">
          <div className="a-filter__search">
            <label className="a-filter__group-title" htmlFor="search">
              Zoek (activiteit of onderwerp):
            </label>
            <input
              className="form__input"
              id="search"
              type="text"
              placeholder="Ik zoek..."
              value={query}
              onChange={e => {
                debouncedPush(push, 'filter-text', e.target.value)
                updateQuery(e.target.value)
              }}
              autoComplete="on"
            />
          </div>
        </div>

        {locationFilter.kind == 'region' && <div className="a-filter__group">
          <label className="a-filter__group-title" htmlFor="activity-loc">
            {locationFilter.heading}
          </label>
          <div className="a-filter__select form__select">
            <select
              id="activity-loc"
              onChange={e => {
                e.preventDefault()
                push('filter-region', e.target.value || locationFilter.defaultLabel)
                updateRegions(e.target.value ? [e.target.value] : [])
              }}
              value={activeRegions.first() || ''}
            >
              <option key="any-location" value={''}>
                {locationFilter.defaultLabel}
              </option>
              {filterOptions.regions.map(region => (
                <option key={`region__${region}`} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        </div>}
        {locationFilter.kind == 'location' && <div className="a-filter__group">
          <label className="a-filter__group-title" htmlFor="activity-loc">
            {locationFilter.heading}
          </label>
          <div className="a-filter__select form__select">
            <select
              id="activity-loc"
              onChange={e => {
                e.preventDefault()
                push('filter-location', e.target.value || locationFilter.defaultLabel)
                updateLocations(e.target.value ? [e.target.value] : [])
              }}
              value={activeLocations.first() || ''}
            >
              <option key="any-location" value={''}>
                {locationFilter.defaultLabel}
              </option>
              {filterOptions.locations.map(location => (
                <option key={`location__${location}`} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </div>
        </div>}
        <div className="a-filter__content">
          <div className="a-filter__group">
            <p className="a-filter__group-title">Onderwerpen</p>
            {filterOptions.tags.map(tag => {
              const checked = activeTags.has(tag)
              const update = () => {
                push(`filter-category-${checked ? 'off' : 'on'}`, tag)
                updateTags((checked ? activeTags.delete(tag) : activeTags.add(tag)).toArray())
              }
              const inputId = `tag-input-${slugify(tag)}`
              const labelId = `tag-label-${slugify(tag)}`

              return (
                <div key={`tag__${tag}`} className={eventsOverviewClassNames.filterCheckbox}>
                  <input
                    id={inputId}
                    className={eventsOverviewClassNames.filterCheckboxInput}
                    type="checkbox"
                    checked={activeTags.has(tag)}
                    name={tag}
                    value={tag}
                    onChange={update}
                    aria-describedby={labelId}
                  />
                  <label
                    id={labelId}
                    className={eventsOverviewClassNames.filterCheckboxLabel}
                    htmlFor={inputId}>
                    {tag}
                  </label>
                </div>
              )
            })}
          </div>
        </div>
        <div className="a-filter__header">
          <h3 className="a-filter__title title--ms">Spring naar</h3>
        </div>
        <div className="a-filter__content">
          <div className="a-filter__group">
            <button className={eventsOverviewClassNames.timelineButton} onClick={() => {
              push('vanaf', 'vandaag')
              updateFrom(new Date())
            }}>
              Vandaag
            </button>
            <button className={eventsOverviewClassNames.timelineButton} onClick={() => {
              push('vanaf', 'morgen')
              updateFrom(tomorrow())
            }}>
              Morgen
            </button>
            <button className={eventsOverviewClassNames.timelineButton} onClick={() => {
              push('vanaf', 'volgende week')
              updateFrom(nextWeek())
            }}>
              Volgende week
            </button>
            <button className={eventsOverviewClassNames.timelineButton} onClick={() => {
              push('vanaf', 'volgende maand')
              updateFrom(nextMonth())
            }}>
              Volgende maand
            </button>
            <DatePicker
              dateFormat="EEEE, dd MMMM yyyy" locale="nl"
              minDate={now} selected={from}
              onChange={newDate => {
                push('vanaf', 'specifieke datum')
                updateFrom(moment(newDate || new Date()).startOf('day').toDate())
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}
